import { Suspense, lazy } from 'react';
import { cookieKeys } from '@logz-pkg/enums';
import { cookiesProvider } from '@logz-pkg/frontend-services';
import { generateLogzTestAttributes } from '@logz-pkg/test-selectors';
import { Box, Flex, Spinner } from '@logz-ui/styleguide';
import styled, { useTheme } from 'styled-components';
import { isRedirectingToNewApp } from '../../../hooks/new-application-redirect/register-new-application';
import { appEmbedded } from '../../../app/configs/app-embedded';
import {
  LazyCancelPaymentBar,
  LazyDataParsingModal,
  LazyLogsPaymentBar,
  LazyLogsSuspensionBar,
  LazyMetricsSuspensionBar,
  LazyTracingSuspensionBar,
} from './AppGlobalComponents';
import { NavigationHeader } from './Navigation/Header/Header';
import { Sidenav } from './Navigation/Sidenav/Sidenav';
import { OnboardingBars } from './OnboardingBars';
import { NewSidenav } from './Navigation/NewSidenav/NewSidenav';
import { AppConfirmModal } from 'ui/components/App/AppConfirmModal';
import { SelectAwsMainAccountModal } from 'ui/components/App/SelectAwsMainAccountModal';
import { GuidedWalkthroughDrawer } from 'ui/components/GuidedWalkthrough/Components/Drawer';
import { ImpersonationBar } from 'ui/components/Impersonation/ImpersonationBar';
import { OnlineChecker } from 'ui/components/OnlineChecker/OnlineChecker';
import { PlaygroundModal } from 'ui/components/Playground/Modal';
import { DataSentModalContainer } from 'ui/components/WelcomeCenter/DataSentModal/Container';
import { useFeatureFlagState, useWelcomeState } from 'ui/state/hooks';
import { useGuidedWalkthrough } from 'ui/state/hooks/guided-walkthrough.state.hook';
import { usePlaygroundState } from 'ui/state/hooks/playground.state.hook';
import { useIsOrionChatAccount } from 'ui/components/OrionChat/use-orion-chat-gates.hook';
import { TemporaryModeBanner } from 'ui/components/App/TemporaryModeBanner';
import { Open360MoveNotice } from 'ui/components/App/Open360MoveNotice/Open360MoveNotice';
import { useOpen360MoveNotice } from 'ui/components/App/Open360MoveNotice/use-open360-move-notice.hook';
import { NewAppActiveModal } from 'ui/components/App/NewAppActiveModal';
import { ExploreSneakPeakBanner, DashboardSneakPeakBanner } from 'ui/components/App/SneakPeakBanners';
import { useIsNewAppEnabled } from 'ui/hooks/is-new-application-enabled.hook';

const Layout = styled(Flex)`
  flex-grow: 1;
  width: 100%;
  overflow: hidden;
`;

const Main = styled(Flex)`
  flex-direction: column;
  overflow: hidden;
  flex: 1;
`;

const Content = styled(Box)`
  display: inline-block;
  flex-grow: 1;
  flex-direction: row;
  position: relative;
  overflow: auto;
  // Eliminate default order for the z-index stack so the header will be above the content.
  z-index: 0;
`;

/**
 * Mounted HERE, not inside the Explore page, so the drawer's containing block is the viewport. Inside Explore it
 * inherited that layout's height (viewport minus header and banners), which left its inner scroll areas — the
 * Context panel above all — too short to be usable.
 *
 * Rendered whenever the account is entitled rather than only on Explore: it keeps polling a run that is still in
 * flight after the user navigates away, which is what `resume-on-mount` depends on. The trigger stays
 * Explore-only (D-B6), so this is invisible elsewhere.
 */
const LazyOrionChatDrawer = lazy(() =>
  import('ui/components/OrionChat/OrionChatDrawer').then(module => ({ default: module.OrionChatDrawer })),
);

const OrionChatDrawer: React.FC = () => {
  const isOrionChatAccount = useIsOrionChatAccount();

  if (!isOrionChatAccount) return null;

  return (
    <Suspense fallback={null}>
      <LazyOrionChatDrawer />
    </Suspense>
  );
};

export const AuthenticatedLayout = ({ children }) => {
  const theme = useTheme();
  const { isActivated: isGuidedWalkthroughActivated } = useGuidedWalkthrough();
  const { isFocusMode, isDataSentModalOpen } = useWelcomeState();
  const { isPlayground } = usePlaygroundState();
  const { isFeatureEnabled: isNewNavigationEnabled } = useFeatureFlagState('NewNavigation');
  const { isNewAppEnabled, isFetched: isNewAppEnabledFetched } = useIsNewAppEnabled();
  const { isFeatureEnabled: isNewAppToggleEnabled } = useFeatureFlagState('NewAppToggle');
  const { isVisible: isMoveNoticeVisible, snooze: snoozeMoveNotice } = useOpen360MoveNotice();

  const showSidenav = !appEmbedded && !isFocusMode && isNewAppEnabledFetched;
  // One Open 360 pitch at a time: the move notice replaces the sneak-peek banners while it shows.
  const showMoveNotice = showSidenav && isMoveNoticeVisible;
  const awsMarketplaceToken = cookiesProvider.getCookie(cookieKeys.AWS_MARKETPLACE_TOKEN);
  const marketplaceToken = cookiesProvider.getCookie(cookieKeys.CLOUD_MARKETPLACE_TOKEN);

  if (!isNewAppEnabledFetched || isRedirectingToNewApp) {
    return <Spinner size="l" fullscreen />;
  }

  return (
    <>
      <OnlineChecker />
      {isPlayground && <PlaygroundModal />}
      {showSidenav && isNewAppToggleEnabled && <TemporaryModeBanner />}
      {showMoveNotice && <Open360MoveNotice onSnooze={snoozeMoveNotice} />}
      {isNewAppToggleEnabled && !showMoveNotice && <DashboardSneakPeakBanner />}
      {isNewAppToggleEnabled && !showMoveNotice && <ExploreSneakPeakBanner />}
      <Layout {...generateLogzTestAttributes({ theme: theme.name })}>
        {isDataSentModalOpen && <DataSentModalContainer />}
        {showSidenav && !isNewNavigationEnabled && <Sidenav />}
        {showSidenav && isNewNavigationEnabled && <NewSidenav />}
        <Main>
          {!isNewAppEnabled && isNewAppEnabledFetched && (
            <>
              <NewAppActiveModal />
              <AppConfirmModal />
              <OnboardingBars />
              <LazyLogsSuspensionBar />
              <LazyTracingSuspensionBar />
              <LazyMetricsSuspensionBar />
              <ImpersonationBar />
              <LazyDataParsingModal />
              <LazyLogsPaymentBar />
              <LazyCancelPaymentBar />
              {awsMarketplaceToken && <SelectAwsMainAccountModal />}
              {marketplaceToken && <SelectAwsMainAccountModal />}
              {!appEmbedded && !isFocusMode && <NavigationHeader />}
            </>
          )}
          <Content>
            {isGuidedWalkthroughActivated ? <GuidedWalkthroughDrawer /> : null}
            {children}
          </Content>
        </Main>
      </Layout>
      <OrionChatDrawer />
    </>
  );
};
